import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional label shown in error UI (e.g. "Catalog page") */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(
      `[ErrorBoundary]${this.props.label ? ` (${this.props.label})` : ''} uncaught error:`,
      error,
      '\nComponent stack:',
      info.componentStack,
    );
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4 p-8 text-center">
        <div className="rounded-xl border border-red-800/40 bg-red-950/30 p-6 max-w-lg w-full">
          <h2 className="text-lg font-semibold text-red-400 mb-2">
            {this.props.label ? `${this.props.label} crashed` : 'Something went wrong'}
          </h2>
          <p className="text-sm text-red-300/80 font-mono break-all mb-4">
            {this.state.error.message}
          </p>
          <button
            onClick={() => {
              this.setState({ error: null });
              window.location.reload();
            }}
            className="px-4 py-2 rounded-lg bg-red-700 hover:bg-red-600 text-white text-sm font-medium transition-colors"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
